import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logAccountActivity } from "@/hooks/useAccountActivity";

export interface SaveNoteArgs {
  companyId: string;
  note: string;
  userId: string | null;
  userName?: string | null;
  /**
   * Whether a rep note already exists on this account. Used only to decide
   * whether to emit `note_added` vs `note_updated` in the activity log.
   */
  isFirstNote?: boolean;
}

/**
 * Saves the rep's note onto `companies.notes` and emits a `note_added` or
 * `note_updated` activity log entry.
 */
export function useSaveAccountNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      companyId,
      note,
      userId,
      userName,
      isFirstNote,
    }: SaveNoteArgs) => {
      const nowIso = new Date().toISOString();
      const update: Record<string, unknown> = {
        notes: note,
        notes_updated_at: nowIso,
        notes_updated_by: userId,
      };
      const { error } = await (supabase as any)
        .from("companies")
        .update(update)
        .eq("id", companyId);
      if (error) throw error;

      const performedBy = { id: userId, name: userName ?? "User" };
      await logAccountActivity({
        accountId: companyId,
        activityType: isFirstNote ? "note_added" : "note_updated",
        description: note.slice(0, 280),
        metadata: { length: note.length },
        performedBy,
      });
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["company-detail", vars.companyId] });
      queryClient.invalidateQueries({ queryKey: ["account-activity-full", vars.companyId] });
    },
  });
}
